import { apiFetch } from './client.js'

function normalizeSegmentation(segmentation) {
  if (!segmentation || typeof segmentation !== 'object') {
    return null
  }

  const groupConfig = segmentation.group_config ?? segmentation.groupConfig
  return {
    id: segmentation.id,
    applicationId: segmentation.application_id || segmentation.applicationId,
    name: segmentation.name || '',
    description: segmentation.description || '',
    isDefault: Boolean(segmentation.is_default ?? segmentation.isDefault),
    requiredPatternTypes: Array.isArray(
      segmentation.required_pattern_types ?? segmentation.requiredPatternTypes,
    )
      ? segmentation.required_pattern_types ?? segmentation.requiredPatternTypes
      : [],
    patterns: Array.isArray(segmentation.patterns) ? segmentation.patterns : [],
    groupConfigId:
      segmentation.group_config_id ??
      segmentation.groupConfigId ??
      groupConfig?.id ??
      null,
    groupConfig: groupConfig
      ? { id: groupConfig.id, name: groupConfig.name || '' }
      : null,
    createdAt: segmentation.created_at || segmentation.createdAt || null,
    updatedAt: segmentation.updated_at || segmentation.updatedAt || null,
  }
}

function extractList(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

function extractItem(payload) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    if (payload.item) return payload.item
    if (payload.data && !Array.isArray(payload.data)) return payload.data
  }
  return payload
}

function hasOwn(source, key) {
  return Object.prototype.hasOwnProperty.call(source, key)
}

function buildCreatePayload(values) {
  const payload = {
    name: String(values?.name || '').trim(),
    description: String(values?.description || '').trim(),
    is_default: Boolean(values?.isDefault),
    required_pattern_types: Array.isArray(values?.requiredPatternTypes)
      ? values.requiredPatternTypes
      : [],
    patterns: Array.isArray(values?.patterns) ? values.patterns : [],
  }
  if (values?.groupConfigId != null && values?.groupConfigId !== '') {
    payload.group_config_id = Number(values.groupConfigId)
  }
  return payload
}

function buildUpdatePayload(values) {
  const payload = {}

  if (hasOwn(values, 'name')) payload.name = String(values.name || '').trim()
  if (hasOwn(values, 'description')) payload.description = String(values.description || '').trim()
  if (hasOwn(values, 'isDefault')) payload.is_default = Boolean(values.isDefault)
  if (hasOwn(values, 'requiredPatternTypes')) {
    payload.required_pattern_types = Array.isArray(values.requiredPatternTypes)
      ? values.requiredPatternTypes
      : []
  }
  if (hasOwn(values, 'patterns')) payload.patterns = Array.isArray(values.patterns) ? values.patterns : []
  if (hasOwn(values, 'groupConfigId')) {
    payload.group_config_id =
      values.groupConfigId != null && values.groupConfigId !== ''
        ? Number(values.groupConfigId)
        : null
  }

  return payload
}

function filterByGroupConfigId(segmentations, groupConfigId) {
  if (groupConfigId == null || groupConfigId === '') return segmentations
  const normalizedGroupConfigId = String(groupConfigId || '').trim()
  return segmentations.filter(
    (item) =>
      String(item?.groupConfigId ?? item?.groupConfig?.id ?? '').trim() === normalizedGroupConfigId,
  )
}

export async function listSegmentations(applicationId, options = {}) {
  const includeDefaults = options.includeDefaults ?? true
  const params = new URLSearchParams()
  params.set('include_defaults', includeDefaults ? 'true' : 'false')
  if (options.groupConfigId != null && options.groupConfigId !== '') {
    params.set('group_config_id', String(options.groupConfigId))
  }
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/segmentations?${params.toString()}`,
  )
  const normalized = extractList(payload).map(normalizeSegmentation).filter(Boolean)
  if (!options.groupConfigId || normalized.length > 0) {
    return normalized
  }

  const fallbackParams = new URLSearchParams()
  fallbackParams.set('include_defaults', includeDefaults ? 'true' : 'false')
  const fallbackPayload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/segmentations?${fallbackParams.toString()}`,
  )
  const fallbackNormalized = extractList(fallbackPayload).map(normalizeSegmentation).filter(Boolean)
  return filterByGroupConfigId(fallbackNormalized, options.groupConfigId)
}

export async function getSegmentation(applicationId, segmentationId) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/segmentations/${encodeURIComponent(segmentationId)}`,
  )
  return normalizeSegmentation(extractItem(payload))
}

export async function createSegmentation(applicationId, values) {
  const payload = await apiFetch(`/apps/${encodeURIComponent(applicationId)}/segmentations`, {
    method: 'POST',
    body: buildCreatePayload(values),
  })
  return normalizeSegmentation(extractItem(payload))
}

export async function updateSegmentation(applicationId, segmentationId, values) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/segmentations/${encodeURIComponent(segmentationId)}`,
    {
      method: 'PATCH',
      body: buildUpdatePayload(values),
    },
  )
  return normalizeSegmentation(extractItem(payload))
}

export async function deleteSegmentation(applicationId, segmentationId) {
  await apiFetch(`/apps/${encodeURIComponent(applicationId)}/segmentations/${encodeURIComponent(segmentationId)}`, {
    method: 'DELETE',
  })
}
